<template>
  <div style="margin: -24px; height: 100vh; display: flex;">

    <div v-if="tour !== undefined && tour.scenes.length === 0" class="no-scenes">
      <div class="no-scenes__content">
        <img src="@/assets/no-scenes.png" width="400" height="400">
        <h1>No scenes yet!</h1>
        <div class="no-scenes__hint">Drag and drop PNG, JPG, JPEG images to create scenes.</div>
      </div>
    </div>

    <v-list v-if="tour !== undefined && tour.scenes.length > 0"
            two-line
            class="pt-3 mt-0 scenes-list">
      <div class="pr-4 pl-4 pb-4 pt-2">
        <v-btn class="w-100 sh-green" color="primary" @click="addScene">
          <v-icon left>
            mdi-plus
          </v-icon>
          Add scene
        </v-btn>
      </div>

      <v-list-item v-for="scene in scenes" :key="scene.id" class="pb-3">
        <scene-card
          :active="currentScene !== undefined && scene.id === currentScene.id"
          :name="scene.name"
          :image="scene.thumbnail || scene.image"
          :is-default="scene.default"
          @click="currentScene = scene"
          @on-remove="remove(scene)"
          @on-default="setDefault(scene)"
          @on-edit="edit(scene)"
        />
      </v-list-item>
    </v-list>

    <pano-viewer
      style="flex: 1;"
      ref="pano"
      :camera-fov="60"
      :min-fov="20"
      :max-fov="70"
      v-if="tour !== undefined && mainScene !== undefined && tour.scenes.length > 0"
      :controlBar="false"
      :main-scene="mainScene.id"
      :auto-hide-infospot="false"
      @dbl-click="dblClick"
      @loadScene="overlay = false"
      @progressScene="overlay = true"
    >
      <pano-scene
        v-for="scene in tour.scenes"
        :key="scene.id"
        :name="scene.id"
        :source="scene.image"
        :center="scene.schema.center"
      >
        <pano-info-spot
          v-if="scene.schema.center.x !== 0 && scene.schema.center.y !== 0 && scene.schema.center.z !== 0"
          label=""
          :icon="require('@/assets/icon-center.png')"
          :position="scene.schema.center"
        />
        <pano-info-spot
          v-for="(point, id) in scene.schema.points"
          :key="`point_${point.id}`"
          :label="point.label"
          :scale="200"
          :icon="typedIcons[point.icon]"
          :position="point.position"
          @click="pointDialog(id)"
        />
        <pano-mesh
          v-for="(polygon, id) in scene.schema.polygons"
          :key="`path_${polygon.id}`"
          :color="polygon.color || '#FFFFFFFF'"
          :hover-color="polygon.hover_color || '#FFFFFFFF'"
          :points="polygon.path"
          @click="polygonDialog(id)"
        />
      </pano-scene>
    </pano-viewer>

    <div :class="{ 'tour-header': true, 'tour-header--mini': tour !== undefined && tour.scenes.length === 0}">
      <v-btn
        class="header-btn"
        fab
        dark
        @click="$router.push({ name: 'cpanel.tours.list' })"
        color="gray">
        <v-icon dark>
          mdi-arrow-left
        </v-icon>
      </v-btn>
      <v-btn
        class="header-btn"
        fab
        dark
        small
        @click="tourSetting"
        color="gray">
        <v-icon dark>
          mdi-cog-outline
        </v-icon>
      </v-btn>

      <div class="tour-title" v-if="tour !== undefined">
        {{ tour.name }}
      </div>
    </div>

    <v-speed-dial
      v-if="currentScene !== undefined"
      style="position: absolute"
      v-model="fab"
      :top="false"
      :bottom="true"
      :right="true"
      :left="false"
      direction="top"
      :open-on-hover="false"
      transition="slide-y-reverse-transition"
    >
      <template v-slot:activator>
        <v-btn @click="cancelAction"
          v-model="fab" color="primary" dark fab>
          <v-icon v-if="fab">mdi-close</v-icon>
          <v-icon v-else>{{ btnIcon }}</v-icon>
        </v-btn>
      </template>
      <v-btn fab dark small color="blue"
             v-show="action === undefined"
             @click="action = 'point'">
        <v-icon>mdi-map-marker</v-icon>
      </v-btn>
      <v-btn fab dark small color="blue"
             v-show="action === undefined"
             @click="makePolygon">
        <v-icon>mdi-map-marker-path</v-icon>
      </v-btn>
      <v-btn fab dark small color="blue"
             v-show="action === undefined"
             @click="action = 'center'">
        <v-icon>mdi-image-filter-center-focus</v-icon>
      </v-btn>
    </v-speed-dial>

    <v-overlay :value="overlay" style="margin-left: 87px;">
      <v-progress-circular
        indeterminate
        size="64"
      ></v-progress-circular>
    </v-overlay>

    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        size="64"
      ></v-progress-circular>
    </v-overlay>

    <drag-and-drop ref="drop" @change="scenesDrop"/>
    <tour-dialog ref="tour" />
    <scene-dialog ref="scene" />
    <confirm-dialog ref="confirm"/>
    <polygon-dialog v-if="currentScene !== undefined" ref="polygonDialog" :scenes="scenes.filter(s => s.id !== currentScene.id)" />
    <point-dialog v-if="currentScene !== undefined" ref="pointDialog" :scenes="scenes.filter(s => s.id !== currentScene.id)" />
  </div>
</template>

<script>
import PanoViewer from '@/components/pano/PanoViewer'
import PanoScene from '@/components/pano/PanoScene'
import PanoInfoSpot from '@/components/pano/PanoInfoSpot'
import PointDialog from '@/components/PointDialog'
import ConfirmDialog from '@/components/ConfirmDialog'
import PanoMesh from '@/components/pano/PanoMesh'
import PolygonDialog from '@/components/PolygonDialog'
import SceneCard from '@/components/SceneCard'
import DragAndDrop from '@/components/DragAndDrop'
import SceneDialog from '../../../components/SceneDialog'
import TourDialog from '../../../components/TourDialog'

export default {
  name: 'scenario',
  components: { TourDialog, SceneDialog, DragAndDrop, SceneCard, PolygonDialog, PanoMesh, ConfirmDialog, PointDialog, PanoInfoSpot, PanoScene, PanoViewer },
  watch: {
    currentScene () {
      if (this.$refs.pano !== undefined) {
        this.$refs.pano.switchScene(this.currentScene.id)
      }
    },
    unsaved () {
      if (this.unsaved) {
        this.saveSchema()
      }
    }
  },
  computed: {
    btnIcon () {
      switch (this.action) {
        case 'point':
        case 'polygon':
        case 'center': return 'mdi-check-all'
        default: return 'mdi-square-edit-outline'
      }
    },
    scenes () {
      if (this.tour === undefined) {
        return []
      }

      return this.tour.scenes
    },
    mainScene () {
      let ms = null
      if (this.tour !== undefined) {
        ms = this.tour.scenes.find(el => el.default)
        if (ms === undefined && this.tour.scenes.length > 0) {
          ms = this.tour.scenes[0]
        }
      }
      return ms
    },
    currentSceneIdx () {
      return this.tour.scenes.findIndex(e => e.id === this.currentScene.id)
    }
  },
  data: () => ({
    fab: false,
    loading: false,
    tour: undefined,
    action: undefined,
    currentScene: undefined,
    unsaved: false,
    overlay: false,
    typedIcons: {
      dot: require('@/assets/icon-dot.png'),
      plus: require('@/assets/icon-plus.png'),
      arrow: require('@/assets/icon-arrow.png'),
      question: require('@/assets/icon-question.png')
    }
  }),
  created () {
    this.id = this.$route.params.id
    this.load(this.id)
  },
  methods: {
    addScene () {
      this.$refs.drop.select()
    },
    load (id) {
      this.loading = true
      this.$api.tours.get(id).then(({ data }) => {
        data.scenes.forEach(s => {
          if (s.schema === null) {
            s.schema = {
              center: { x: 0, y: 0, z: 0 },
              points: [],
              polygons: []
            }
          } else {
            if (s.schema.points === null) {
              s.schema.points = []
            }
            if (s.schema.polygons === null) {
              s.schema.polygons = []
            }
          }
        })
        if (data.scenes.length > 0) {
          this.currentScene = data.scenes.find(s => s.default === true)

          if (this.currentScene === undefined) {
            this.currentScene = data.scenes[0]
          }
        }
        this.tour = data
      }).catch(() => {
        this.$router.push({ name: 'cpanel.tours.list' })
      }).finally(() => {
        this.loading = false
      })
    },
    saveSchema () {
      this.$api.tours.sceneSave(this.tour.id, this.currentScene.id, {
        name: this.tour.scenes[this.currentSceneIdx].name,
        description: this.tour.scenes[this.currentSceneIdx].description,
        default: this.tour.scenes[this.currentSceneIdx].default,
        schema: this.tour.scenes[this.currentSceneIdx].schema
      }).then(() => {
        this.unsaved = false
      })
      this.action = undefined
    },
    cancelAction () {
      this.unsaved = true
      this.action = undefined
    },
    makePolygon () {
      this.action = 'polygon'
      if (!this.tour.scenes[this.currentSceneIdx].schema.polygons) {
        this.tour.scenes[this.currentSceneIdx].schema.polygons = []
      }
      this.tour.scenes[this.currentSceneIdx].schema.polygons.push({
        id: Date.now(),
        color: '#007EFFFF',
        hover_color: '#007EFF96',
        on_click: {
          action: '',
          value: ''
        },
        path: []
      })
    },
    dblClick (position) {
      if (this.tour.scenes[this.currentSceneIdx].schema === null) {
        this.tour.scenes[this.currentSceneIdx].schema = {
          center: { x: 0, y: 0, z: 0 },
          points: [],
          polygons: []
        }
      }

      if (this.action === 'polygon') {
        const idx = this.tour.scenes[this.currentSceneIdx].schema.polygons.length
        this.tour.scenes[this.currentSceneIdx].schema.polygons[idx - 1].path.push(position)
      }

      if (this.action === 'center') {
        this.tour.scenes[this.currentSceneIdx].schema.center = position
        this.unsaved = true
      }

      if (this.action === 'point') {
        this.tour.scenes[this.currentSceneIdx].schema.points.push({
          id: Date.now(),
          label: '',
          icon: 'dot',
          on_click: {
            action: '',
            value: ''
          },
          position: position
        })
        this.unsaved = true
      }
    },
    pointDialog (id) {
      this.$refs.pointDialog.open(this.tour.scenes[this.currentSceneIdx].schema.points[id]).then(point => {
        if (point === 'remove') {
          this.$refs.confirm.open('Remove point?', 'Do you want to remove this point?').then((data) => {
            if (data) {
              this.tour.scenes[this.currentSceneIdx].schema.points.splice(id, 1)
              this.unsaved = true
              this.$forceUpdate()
            }
          })
        } else if (point === 'cancel') {
        } else {
          this.unsaved = true
          this.tour.scenes[this.currentSceneIdx].schema.points[id].label = point.label
          this.tour.scenes[this.currentSceneIdx].schema.points[id].icon = point.icon
          this.tour.scenes[this.currentSceneIdx].schema.points[id].on_click = point.on_click
        }
      })
    },
    polygonDialog (id) {
      if (this.action !== undefined) {
        return
      }

      this.$refs.polygonDialog.open(this.tour.scenes[this.currentSceneIdx].schema.polygons[id]).then(polygon => {
        if (polygon === 'remove') {
          this.$refs.confirm.open('Remove polygon?', 'Do you want to remove this polygon?').then((data) => {
            if (data) {
              this.tour.scenes[this.currentSceneIdx].schema.polygons.splice(id, 1)
              this.unsaved = true
              this.$forceUpdate()
            }
          })
        } else if (polygon === 'cancel') {
        } else {
          this.unsaved = true
          this.tour.scenes[this.currentSceneIdx].schema.polygons[id].color = polygon.color
          this.tour.scenes[this.currentSceneIdx].schema.polygons[id].hover_color = polygon.hover_color
          this.tour.scenes[this.currentSceneIdx].schema.polygons[id].on_click = polygon.on_click
        }
      })
    },
    remove (scene) {
      this.$refs.confirm.open('Remove scene?', `Do you want to remove scene "${scene.name}"?`).then((data) => {
        if (data) {
          this.$api.tours.removeScene(this.id, scene.id).then(() => {
            this.tour.scenes.splice(this.tour.scenes.findIndex(e => e.id === scene.id), 1)
          })
        }
      })
    },
    tourSetting () {
      this.$refs.tour.open(this.tour).then((data) => {
        if (data === 'remove') {
          this.$refs.confirm.open('Remove tour?', `Do you want to remove tour "${this.tour.name}"?`).then((data) => {
            if (data) {
              this.$api.tours.remove(this.tour.id).then(() => {
                this.$router.push({ name: 'cpanel.tours.list' })
              })
            }
          })
        } else if (data !== 'cancel') {
          this.$api.tours.edit(this.id, data).then(() => {
            this.tour = data
            this.$forceUpdate()
          })
        }
      })
    },
    edit (scene) {
      this.$refs.scene.open(scene).then((data) => {
        if (data === 'remove') {
          this.remove(scene)
        } else if (data !== 'cancel') {
          this.$api.tours.sceneEdit(this.id, scene.id, data).then(() => {
            this.tour.scenes[this.tour.scenes.findIndex(s => s.id === scene.id)] = data
            this.$forceUpdate()
          })
        }
      })
    },
    setDefault (scene) {
      this.$api.tours.sceneSetDefault(this.id, scene.id, scene).then(() => {
        this.load(this.id)
      })
    },
    scenesDrop (files) {
      let cn = 0
      let first = this.tour.scenes.length === 0
      this.loading = true
      files.forEach((file) => {
        this.$api.tours.createScene(this.id, {
          name: `${file.name.split('.')[0]}`
        }).then(({ data }) => {
          // TODO: FIX
          this.$api.tours.sceneImage(this.id, data.id, file).then(() => {
            if (first) {
              this.$api.tours.sceneSetDefault(this.id, data.id, data)
              first = false
            }
          })
        }).finally(() => {
          this.$forceUpdate()
          cn++
          if (cn >= files.length) {
            this.load(this.id)
            this.loading = false
          }
        })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.tour-header {
  position: absolute;
  top: 24px;
  left: 297px;
  height: 48px;
  right: 15px;
  display: flex;
  align-items: center;

  &--mini {
    left: 25px;
  }

  .header-btn {
    margin-right: 15px;
  }

  .tour-title {
    color: #FFFFFF;
    font-size: 1.5rem;
    font-weight: 600;
    text-shadow: 1px 1px 15px #000;
    user-select: none;
  }
}

.scenes-list {
  backdrop-filter: blur(6px);
  background-color: #0000006e;
  width: 280px;
  overflow-y: scroll;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
}

.no-scenes {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  user-select: none;
  background: linear-gradient(250.16deg, #ffffff 2.51%, rgba(213, 241, 255, 0.54) 96.8%);

  &__content {
    max-width: 500px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    top: -50px;
    position: relative;

    h1 {
      font-size: 3.3rem;
      font-weight: 400;
    }
    img {
      width: 100%;
      height: auto;
      pointer-events: none;
    }
  }

  &__hint {
    font-size: 1.8rem;
    text-align: center;
    font-weight: 300;
  }
}
</style>
